import LoginLayout from '@/layout/LoginLayout';
import { trpc } from '@/trpcClient';
import {
  checkVerificationCodeReceiveInput,
  VerificationCodeReceiveInputType,
} from '@shelflix/schemas/authentication/verificationReceive';
import Icon from '@shelflix/ui-web/components/Icon';
import { Button } from '@shelflix/ui-web/components/ui/button';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  scrollToError,
} from '@shelflix/ui-web/components/ui/form';
import { Input } from '@shelflix/ui-web/components/ui/input';
import { RadioGroup, RadioGroupItem } from '@shelflix/ui-web/components/ui/radio-group';
import { useMutation } from '@tanstack/react-query';
import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { useVerificationReceiveStore } from './store';
import { logger } from '@/utils/logger';
import { extractTenantSlug, getTenantDomain } from '@/utils/tenantDetection';

export default function LoginVerificationCodeReceive() {
  const { formData, resetForm } = useVerificationReceiveStore();
  const verificationCodeReceiveMutation = useMutation(
    trpc.authentication['verification-code-receive'].mutationOptions()
  );
  const checkRegistrationMutation = useMutation(trpc.authentication['check-registration'].mutationOptions());
  const checkPhoneMutation = useMutation(trpc.authentication['check-phone'].mutationOptions());
  const [searchParams] = useSearchParams();
  const { sendCode } = useLocation().state || { sendCode: false };
  const isFirstAccess = searchParams.has('firstAccess');
  const title = isFirstAccess ? 'Primeiro acesso' : 'Recuperar senha';
  const [identifierType, setIdentifierType] = useState<'email' | 'registration' | 'whatsapp'>('email');

  // Verificar se há email na URL (redirecionamento da tela de configuração)
  const emailFromUrl = searchParams.get('email');

  // Se há email na URL, definir tipo como email
  React.useEffect(() => {
    if (emailFromUrl) {
      if (sendCode) {
        toast.success('Novo código de verificação enviado');
      } else {
        toast.error(`Erro inesperado ao enviar código de verificação`);
      }

      setIdentifierType('email');
    }
  }, [emailFromUrl]);

  const navigate = useNavigate();

  const form = useForm<VerificationCodeReceiveInputType>({
    defaultValues: {
      ...formData,
      // NOTE: 'email' field is a generic identifier - can be email, registration, or phone number
      email: emailFromUrl || formData.email || '',
    },
    mode: 'onSubmit',
  });

  const onSubmit = (data: VerificationCodeReceiveInputType) => {
    if (
      verificationCodeReceiveMutation.isPending ||
      checkRegistrationMutation.isPending ||
      checkPhoneMutation.isPending
    )
      return;

    // If WhatsApp is selected, validate phone exists in database first
    if (isFirstAccess && identifierType === 'whatsapp') {
      const phone = data.email.replaceAll(/[^\d]/g, ''); // Remove mask characters

      checkPhoneMutation.mutate(
        { phone },
        {
          onSuccess: (result) => {
            if (!result.exists) {
              toast.error('Telefone não encontrado');
              return;
            }

            if (!result.hasEmail) {
              // If user doesn't have email, redirect to contact info configuration
              navigate(`/update-contact-info?phone=${phone}`);
              return;
            }

            // Log the z-api request body for debugging
            const isStaging =
              window.location.hostname.includes('hml') ||
              window.location.hostname.includes('staging') ||
              import.meta.env.MODE === 'staging';
            const messagePrefix = isStaging ? '[HML] ' : '';

            const zapiRequestBody = {
              phone: `55${phone}`,
              message: `${messagePrefix}🔐 *Shelflix - Código de Verificação*\n\nSeu código para primeiro acesso é:\n\n*[CODE]*\n\n⚠️ Este código expira em 60 minutos.\n\n🔒 Não compartilhe este código com ninguém.\n\nSe você não solicitou este código, ignore esta mensagem.`,
            };

            logger.info('[DEBUG] Z-API request that will be sent:', zapiRequestBody);

            // Phone has email, proceed with WhatsApp verification via backend
            const payload = { email: phone, isFirstAccess };
            proceedWithVerification(payload);
          },
          onError: (error) => {
            toast.error(`Erro ao verificar telefone: ${error.message}`);
          },
        }
      );
      return;
    }

    // Se for primeiro acesso e selecionou matrícula, verificar se existe e se tem email
    if (isFirstAccess && identifierType === 'registration') {
      // Get tenant slug from current domain for multi-tenant isolation
      const tenantSlug = extractTenantSlug(getTenantDomain());

      checkRegistrationMutation.mutate(
        { registration: data.email, tenantSlug },
        {
          onSuccess: (result) => {
            if (!result.exists) {
              toast.error('Matrícula não encontrada');
              return;
            }

            if (!result.hasEmail) {
              // Se não tem email, redirecionar para tela de configuração
              navigate(`/update-contact-info?registration=${data.email}`);
              return;
            }

            // Se tem email, continuar com o fluxo normal
            proceedWithVerification(data);
          },
          onError: (error) => {
            toast.error(`Erro ao verificar matrícula: ${error.message}`);
          },
        }
      );
    } else {
      // Fluxo normal para email ou recuperação de senha
      proceedWithVerification(data);
    }
  };

  const proceedWithVerification = (data: VerificationCodeReceiveInputType) => {
    // Get tenant slug from current domain for multi-tenant isolation
    const tenantSlug = extractTenantSlug(getTenantDomain());

    const payload = {
      ...data,
      isFirstAccess,
      tenantSlug,
    };

    verificationCodeReceiveMutation.mutate(payload, {
      onSuccess: async (success) => {
        localStorage.setItem('email', payload.email);
        localStorage.setItem('identifierType', identifierType);
        const successRedirectRoute = success.hasPasswordDefined
          ? '/verification-validate'
          : '/verification-validate?firstAccess';
        navigate(successRedirectRoute);
        resetForm();
      },
      onError: (error) => {
        toast.error(`Erro inesperado ao enviar código de verificação: ${error.message}`);
      },
    });
  };

  return (
    <LoginLayout>
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit, scrollToError)} className="flex flex-col gap-4">
          <div className="mb-4 text-center">
            <h2 className="text-lg font-bold text-[var(--primary)]">{title}</h2>
            <div className="mx-auto mt-4 max-w-xs text-sm text-gray-700">
              {isFirstAccess
                ? 'Escolha como deseja se identificar para receber um código de verificação:'
                : 'Digite seu e-mail para receber um código de verificação:'}
            </div>
          </div>

          {isFirstAccess && (
            <div className="mb-4">
              <RadioGroup
                value={identifierType}
                onValueChange={(value) => setIdentifierType(value as 'email' | 'registration' | 'whatsapp')}
                className="flex gap-4"
              >
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="email" id="email" />
                  <label htmlFor="email" className="text-sm font-medium text-gray-700">
                    E-mail
                  </label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="registration" id="registration" />
                  <label htmlFor="registration" className="text-sm font-medium text-gray-700">
                    Matrícula
                  </label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="whatsapp" id="whatsapp" />
                  <label htmlFor="whatsapp" className="text-sm font-medium text-gray-700">
                    WhatsApp
                  </label>
                </div>
              </RadioGroup>
            </div>
          )}

          <FormField
            control={form.control}
            name="email"
            rules={{ validate: (value) => checkVerificationCodeReceiveInput('email', value?.toLowerCase()) }}
            render={({ field }) => (
              <FormItem>
                <FormLabel className="text-sm font-medium text-gray-700">
                  {isFirstAccess && identifierType === 'registration'
                    ? 'Matrícula'
                    : isFirstAccess && identifierType === 'whatsapp'
                      ? 'WhatsApp'
                      : 'Login'}
                </FormLabel>
                <FormControl>
                  <div className="relative mt-1 flex items-center">
                    <span className="material-symbols-outlined pointer-events-none absolute left-3 text-base text-gray-400">
                      {isFirstAccess && identifierType === 'registration'
                        ? 'badge'
                        : isFirstAccess && identifierType === 'whatsapp'
                          ? 'smartphone'
                          : 'person'}
                    </span>
                    <Input
                      type={
                        isFirstAccess && identifierType === 'registration'
                          ? 'text'
                          : isFirstAccess && identifierType === 'whatsapp'
                            ? 'tel'
                            : 'email'
                      }
                      id="email"
                      placeholder={
                        isFirstAccess && identifierType === 'registration'
                          ? 'Matrícula'
                          : isFirstAccess && identifierType === 'whatsapp'
                            ? '(99) 99999-9999'
                            : 'E-mail'
                      }
                      className="pl-9"
                      {...field}
                      {...(isFirstAccess && identifierType === 'whatsapp' ? { mask: '(99) 99999-9999' } : {})}
                    />
                  </div>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <Button
            type="submit"
            loading={
              verificationCodeReceiveMutation.isPending ||
              checkRegistrationMutation.isPending ||
              checkPhoneMutation.isPending
            }
            className="w-full bg-[var(--primary)]"
          >
            {verificationCodeReceiveMutation.isPending ||
            checkRegistrationMutation.isPending ||
            checkPhoneMutation.isPending ? (
              <div className="flex items-center justify-center space-x-2">
                <div className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                <span>Carregando...</span>
              </div>
            ) : (
              <>
                <Icon name={isFirstAccess && identifierType === 'whatsapp' ? 'smartphone' : 'email'} className="mr-2" />
                Enviar código de verificação
              </>
            )}
          </Button>

          {isFirstAccess && (
            <Button
              type="button"
              variant="outline"
              className="w-full border-[var(--primary)] text-[var(--primary)] hover:bg-[var(--secondary)]"
              onClick={() => navigate('/verification-validate?firstAccess&hasCode=true')}
            >
              <Icon name="key" className="mr-2" />
              Já possuo um código de verificação
            </Button>
          )}

          <div className="h-10 w-full" />
        </form>
      </Form>
    </LoginLayout>
  );
}
